/**
 * Standard-Endpunkte für eine Einbindung im Magento-Document-Root.
 *
 * Bei Store-Codes, Unterverzeichnissen oder abweichenden Routen kann Magento
 * dieselben Werte über window.KOTORI_PRODUCTFINDER_CONFIG überschreiben.
 */
const endpointPaths = {
  treeUrl: 'kotori_productfinder/data/index?format=tree&cache=1',
  productsUrl: 'kotori_productfinder/data/products',
  analyticsUrl: 'kotori_productfinder/analytics/track',
  colourOptionsUrl: 'kotori_farbproduktoptionen/options',
  colourPriceUrl: 'kotori_farbproduktoptionen/options/price',
  cartAddUrl: 'kotori_cartadd/cart/add'
};

function config() {
  const custom = window.KOTORI_PRODUCTFINDER_CONFIG || {};
  // BASE_URL wird von Magento bereitgestellt und enthält gegebenenfalls den
  // Store-Code. Dadurch bleiben alle Standardrouten scopespezifisch erreichbar.
  const baseUrl = custom.baseUrl || window.BASE_URL || `${window.location.origin}/`;
  const normalizedBase = new URL(String(baseUrl).endsWith('/') ? baseUrl : `${baseUrl}/`, window.location.origin);
  const generated = Object.fromEntries(
    Object.entries(endpointPaths).map(([key, path]) => [key, new URL(path, normalizedBase).toString()])
  );
  return {...generated, ...custom};
}

/** Gemeinsamer JSON-Abruf mit Session-Cookies und verständlichem HTTP-Fehler. */
async function fetchJson(url, options = {}, strict = false) {
  const response = await fetch(url, {credentials: 'same-origin', ...options, headers: {'Accept': 'application/json', ...(options.headers || {})}});
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  // Warenkorbantworten dürfen keine HTML-Zielseite nach einer Weiterleitung
  // sein. Ein erneuter POST wäre bei unklarem Ergebnis möglicherweise doppelt.
  if (strict && (response.redirected || !/^application\/(?:[\w.-]+\+)?json(?:\s*;|$)/i.test(response.headers.get('Content-Type') || ''))) {
    throw new Error('Unexpected response');
  }
  return response.json();
}

/** Hängt ausschließlich gesetzte Werte URL-kodiert an einen Endpunkt an. */
function withParams(url, values) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  return `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
}

export async function loadTree() {
  const data = await fetchJson(config().treeUrl);
  return {tree: data.tree || {}, permalink: data.permalink || {}};
}

export async function loadProducts(skus) {
  const data = await fetchJson(withParams(config().productsUrl, {skus: skus.join(',')}));
  if (!data.success) throw new Error(data.message || 'Product data unavailable');
  return data;
}

/**
 * Meldet ein rein fachliches Ereignis an Magento. Die Anfrage enthält keine
 * Browser-, Kunden- oder Sessionkennung; Fehler der Statistik dürfen die
 * Bedienung des Produktfinders niemals unterbrechen.
 */
export function trackEvent(eventType, selection = {}, productSku = '') {
  const body = new URLSearchParams({
    event_type: eventType,
    scope_code: selection.scope || '',
    problem_code: selection.problem || '',
    age_code: selection.age || '',
    material_code: selection.material || '',
    product_sku: productSku || ''
  });

  return fetch(config().analyticsUrl, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: {'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'},
    body: body.toString()
  }).catch(() => undefined);
}

/**
 * Die erlaubten Farbkataloge können produktspezifisch eingeschränkt sein.
 * Deshalb gehört neben der Farbart immer auch die SKU zum Cache-Schlüssel.
 * Preise werden ausdrücklich nicht gecacht, da sie vom Warenkorb abhängen.
 */
const colourCache = new Map();
export async function loadColourOptions(sku, type) {
  const cacheKey = `${String(sku || '').toLowerCase()}:${type}`;
  if (!colourCache.has(cacheKey)) {
    colourCache.set(cacheKey, fetchJson(withParams(config().colourOptionsUrl, {sku, type}))
      .then((data) => {
        if (!data.success) throw new Error(data.message || 'Colour data unavailable');
        return data.groups?.[type] || [];
      })
      .catch((error) => { colourCache.delete(cacheKey); throw error; }));
  }
  return colourCache.get(cacheKey);
}

export function loadColourPrice(sku, qty, colour, express) {
  // Dieser Endpunkt ist die verbindliche Preisquelle. Er kennt bereits im
  // Warenkorb vorhandene konkrete Sonderfarben und berechnet CHF 25/CHF 0 sowie
  // den optionalen CHF-50-Expressaufschlag nach den zentralen Shopregeln.
  return fetchJson(withParams(config().colourPriceUrl, {sku, qty, kotori_colour_key: colour?.key, kotori_colour_express: express ? '1' : ''}));
}

export async function addItemsToCart(items, context = {}) {
  // Den Schlüssel unmittelbar beim Klick lesen; der Produktdaten-Kontext
  // kann noch aus einer inzwischen abgelaufenen Sitzung stammen.
  const body = new URLSearchParams({form_key: getFormKey(), items: JSON.stringify(items)});
  let data;
  try {
    data = await fetchJson(context.cart_add_url || config().cartAddUrl, {method: 'POST', headers: {'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'}, body: body.toString()}, true);
    if (!data || typeof data.success !== 'boolean') throw new Error('Invalid cart response');
  } catch (cause) {
    // Netzwerk-, HTTP- und Parsefehler sagen nicht aus, ob bereits gespeichert
    // wurde. Die Oberfläche fordert daher zuerst zur Warenkorbprüfung auf.
    const error = new Error('Cart confirmation unavailable');
    error.translationKey = 'cartConfirmationError';
    throw error;
  }
  if (!data.success) throw new Error(data.message || 'Cart update failed');
  // FloatingCart lauscht auf dieses Ereignis und erhält die vollständigen,
  // serverseitig berechneten Warenkorbdaten aus Kotori_UrlCartAdd.
  document.dispatchEvent(new CustomEvent('kotori:cart-add-success', {detail: data}));
  // Hyvä aktualisiert daraufhin die native private-content-Warenkorbsektion.
  window.dispatchEvent(new CustomEvent('reload-customer-section-data'));
  return data;
}

export function getFormKey() {
  // Reihenfolge: Hyvä-Helfer, aktuelles Cookie, Magento-Global. Damit funktioniert der
  // gleiche Build sowohl eingebettet als auch in einer lokalen Testumgebung.
  if (window.hyva && typeof window.hyva.getFormKey === 'function') {
    const key = window.hyva.getFormKey();
    if (key) return key;
  }
  const match = document.cookie.match(/(?:^|;\s*)form_key=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : (window.FORM_KEY || '');
}
